import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useAdminConsent } from './useAdminConsent';
import {
  checkDeepScanEligibility,
  startDeepAnalysis,
  getDeepAnalysisStatus,
  downloadDeepReport,
} from '../services/deepAnalysisApi';
import type {
  AnalysisStatus,
  AnalysisStep,
  Industry,
  UserContext,
} from '@/features/discovery/types/discovery.types';

const POLL_INTERVAL_MS = 2500;

interface DeepAnalysisState {
  // Eligibility
  isEligible: boolean | null;
  isCheckingEligibility: boolean;

  // Analysis state
  analysisId: string | null;
  status: AnalysisStatus | null;
  currentStep: AnalysisStep | null;
  stepsCompleted: AnalysisStep[];
  progressPct: number;
  message: string;
  error: string | null;

  // Report
  reportReady: boolean;
  score: number | null;
  tier: string | null;
}

/**
 * Main hook for the Deep Analysis feature.
 * Handles: eligibility check -> admin consent -> start analysis -> poll -> download
 */
export function useDeepAnalysis() {
  const { user, getAccessToken } = useAuth();
  const { acquireApiToken, triggerAdminConsent, isAcquiring } = useAdminConsent();

  const [state, setState] = useState<DeepAnalysisState>({
    isEligible: null,
    isCheckingEligibility: true,
    analysisId: null,
    status: null,
    currentStep: null,
    stepsCompleted: [],
    progressPct: 0,
    message: '',
    error: null,
    reportReady: false,
    score: null,
    tier: null,
  });

  const [apiToken, setApiToken] = useState<string | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Check eligibility on mount
  useEffect(() => {
    let cancelled = false;

    async function checkEligibility() {
      const token = await getAccessToken();
      if (!token || cancelled) {
        setState((s) => ({ ...s, isCheckingEligibility: false, isEligible: false }));
        return;
      }

      try {
        const result = await checkDeepScanEligibility(token);
        if (!cancelled) {
          setState((s) => ({
            ...s,
            isCheckingEligibility: false,
            isEligible: result.deepScanEnabled,
          }));
        }
      } catch {
        if (!cancelled) {
          setState((s) => ({
            ...s,
            isCheckingEligibility: false,
            isEligible: false,
          }));
        }
      }
    }

    checkEligibility();
    return () => { cancelled = true; };
  }, [getAccessToken]);

  // Start analysis
  const startAnalysis = useCallback(
    async (industry: Industry, userContext: UserContext) => {
      setState((s) => ({ ...s, error: null }));

      const token = await acquireApiToken();
      if (!token) return;

      setApiToken(token);

      try {
        const response = await startDeepAnalysis(
          industry,
          userContext,
          token,
          true // always store deep analysis reports
        );
        setState((s) => ({
          ...s,
          analysisId: response.analysisId,
          status: 'pending',
        }));
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Failed to start analysis';
        setState((s) => ({ ...s, error: msg }));
      }
    },
    [acquireApiToken]
  );

  // Poll status
  useEffect(() => {
    if (!state.analysisId || !apiToken) return;
    if (state.status === 'completed' || state.status === 'failed') return;

    const poll = async () => {
      try {
        const status = await getDeepAnalysisStatus(state.analysisId!, apiToken);

        setState((s) => ({
          ...s,
          status: status.status,
          currentStep: status.currentStep,
          stepsCompleted: status.stepsCompleted,
          progressPct: status.progressPct,
          message: status.message,
          error: status.error,
          reportReady: status.status === 'completed',
          score: status.score,
          tier: status.tier,
        }));

        if (status.status === 'completed' || status.status === 'failed') {
          if (pollRef.current) {
            clearInterval(pollRef.current);
            pollRef.current = null;
          }
        }
      } catch (err) {
        console.error('Deep analysis status poll failed:', err);
      }
    };

    poll();
    pollRef.current = setInterval(poll, POLL_INTERVAL_MS);

    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [state.analysisId, state.status, apiToken]);

  // Download report
  const downloadReport = useCallback(async () => {
    if (!state.analysisId || !apiToken) return;
    try {
      await downloadDeepReport(state.analysisId, apiToken);
    } catch (err) {
      console.error('Failed to download report:', err);
    }
  }, [state.analysisId, apiToken]);

  // Reset
  const reset = useCallback(() => {
    setState((s) => ({
      ...s,
      analysisId: null,
      status: null,
      currentStep: null,
      stepsCompleted: [],
      progressPct: 0,
      message: '',
      error: null,
      reportReady: false,
      score: null,
      tier: null,
    }));
    setApiToken(null);
  }, []);

  return {
    ...state,
    isAcquiring,
    startAnalysis,
    downloadReport,
    triggerAdminConsent,
    reset,
    user,
  };
}
